import { Action, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';

import { GetCurrentAddOnsPayload, GetPlanOffersPayload, ProductInstanceStatus } from '@mfe/shared/schema-types';

import { selectAddOns } from '../addOns';
import { selectUser } from '../auth';
import {
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutationWithErrors,
  graphqlQueryWithErrors,
} from '../graphql';
import { selectUserInfo } from '../userInfo';
import {
  addPlanToCart,
  ChangePlanSteps,
  getEicMessages,
  getHasPendingTransition,
  getPlans,
  refetchPlans,
  saveCart,
  selectChangePlan,
  setChangePlanError,
  setChangePlanStep,
  setEicMessages,
  setHasActivationError,
  setHasPendingTransition,
  setHasPendingTransitionError,
  setOrderSuccess,
  setPlans,
  setSelectedPlanId,
  submitOrder,
  TransitionScheduleInput,
} from './changePlanSlice';
import {
  CONFIGURE_CART,
  GET_CHANGE_PLAN_OFFERS,
  GET_EIC_MESSAGES,
  GET_HAS_PENDING_TRANSITION,
  TRANSITION_PLAN,
} from './requests';
import { waitForToken } from '../utils/utilsSagas';
import { ProductInstanceTypes } from '../../../../platform/src/server/dataSources/PSM/types';
import { ADDON_KIND } from '../../constants';

export function* fetchChangePlanOffers(payload?: { refetchData?: boolean }) {
  yield call(waitForToken);

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_CHANGE_PLAN_OFFERS,
    variables: {
      refetchData: payload?.refetchData,
    },
    fetchPolicy: payload?.refetchData ? 'no-cache' : 'cache-first',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setChangePlanError(runtimeError ?? errors));
    return;
  }

  if (data?.getPlanOffers) {
    yield put(setPlans(data.getPlanOffers as GetPlanOffersPayload[]));
  }
}

export function* refetchChangePlanOffers() {
  yield fetchChangePlanOffers({ refetchData: true });
}

export function* addToCart(action: PayloadAction<{ newProductTypeId: string }>) {
  const { newProductTypeId } = action.payload;

  yield put(setChangePlanStep(ChangePlanSteps.ORDER_REVIEW));
  yield put(setSelectedPlanId(newProductTypeId));

  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: CONFIGURE_CART,
    variables: {
      input: {
        newProductTypeId,
      },
    },
  });
  const { data, errors, runtimeError } = response;

  if (runtimeError) {
    yield put(setChangePlanError(runtimeError));
    return;
  }

  if (errors) {
    yield put(setChangePlanError(errors));
    return;
  }

  if (data?.configureCart) {
    const cart = data.configureCart;
    const { id: cartId } = cart;

    yield put(saveCart(cart));
    yield put(getEicMessages({ cartId }));

    const { currentAddOns } = yield select(selectAddOns);

    const hasStaticIP = currentAddOns?.find((addOn: GetCurrentAddOnsPayload) => addOn.kind === ADDON_KIND.STATIC_IP);

    if (data.configureCart.droppedAddOns.length || hasStaticIP)
      yield put(setChangePlanStep(ChangePlanSteps.OPTIONAL_SERVICE_WARNING));
  }
}

type SubmitOrderAction = Action<'changePlan/submitOrder'> & {
  payload?: TransitionScheduleInput;
};

export function* placeOrder(action: SubmitOrderAction) {
  yield put(setChangePlanStep(ChangePlanSteps.ORDER_OUTCOME));
  const { cart, selectedPlanId: newProductTypeId }: ReturnType<typeof selectChangePlan> = yield select(
    selectChangePlan
  );
  const {
    user: { productTypeId: currentProductTypeId },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const workOrderInformation = action?.payload;
  const input = {
    cartId: cart.id,
    workOrderInformation,
    currentProductTypeId,
    newProductTypeId,
  };

  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: TRANSITION_PLAN,
    variables: { input },
  });

  const { data, errors, runtimeError } = response;

  if (runtimeError || errors) {
    yield put(setChangePlanError(runtimeError ?? errors));
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label:
        AnalyticsEventNames.ChangePlanOrderFailed +
        ' | currentPlan: ' +
        cart.cartItems.plan?.name +
        ' | planPrice: ' +
        cart.cartItems.plan?.price,
      event_category: Categories.ChangePlan,
    });
    return;
  }

  if (data?.transitionPlan) {
    yield put(
      setOrderSuccess({
        isSuccess: true,
        appointment: data.transitionPlan.appointment ?? null,
      })
    );
    yield put(setHasPendingTransition(true));
    yield put(getHasPendingTransition());

    yield call(
      [sessionStorageManager, sessionStorageManager.setItem],
      SESSION_STORAGE_VARIABLES.CHANGE_PLAN_SUCCESS,
      true
    );

    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label:
        AnalyticsEventNames.ChangePlanOrderSuccess +
        ' | currentPlan: ' +
        cart.cartItems.plan?.name +
        ' | planPrice: ' +
        cart.cartItems.plan?.price,
      event_category: Categories.ChangePlan,
    });

    yield put(setChangePlanStep(ChangePlanSteps.ORDER_OUTCOME));
  }
}

export function* fetchHasPendingTransition() {
  yield call(waitForToken);

  const {
    user: { productKind, isSoftSuspended, productInstanceStatus, productInstanceId },
  } = yield select(selectUser);

  const {
    userInfo: { isPreInstall },
  } = yield select(selectUserInfo);

  const isSuspended = productInstanceStatus === ProductInstanceStatus.Suspended || isSoftSuspended;
  const isDisconnected =
    productInstanceStatus === ProductInstanceStatus.Deactivated ||
    productInstanceStatus === ProductInstanceStatus.Canceled;
  const hasErrors =
    productInstanceStatus === ProductInstanceStatus.Other || productInstanceStatus === ProductInstanceStatus.Error;

  if (
    ProductInstanceTypes.WirelessInternet === productKind ||
    isSuspended ||
    isDisconnected ||
    isPreInstall ||
    hasErrors
  ) {
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label: `${AnalyticsEventNames.ChangePlanNotSupported} | ProductKind: ${productKind}`,
      event_category: Categories.ChangePlan,
    });

    return;
  }

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_HAS_PENDING_TRANSITION,
    fetchPolicy: 'network-only',
    variables: { productInstanceId, refetchData: true },
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    setHasPendingTransitionError(errors ?? runtimeError);
  }

  if (data?.getHasPendingTransition) {
    const { isChangePlanDisabled, hasActivationError } = data.getHasPendingTransition;
    yield put(setHasPendingTransition(isChangePlanDisabled));
    if (hasActivationError) {
      yield put(setHasActivationError(hasActivationError));
    }
  }
}

export function* getEicMessagesSaga(action: PayloadAction<{ cartId: string }>) {
  const {
    cart: { eicRequired },
  } = yield select(selectChangePlan);

  if (!eicRequired) {
    yield put(setEicMessages({ loading: false, messages: [] }));
    return;
  }

  const { cartId } = action.payload;

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_EIC_MESSAGES,
    variables: { input: { shoppingCartId: cartId } },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors || !data) {
    console.error('GraphQL Error:', runtimeError || errors);
    yield put(setChangePlanError(runtimeError ?? errors));
    return;
  }

  if (data.getEicMessage?.eicMessage?.length) {
    yield put(setEicMessages(data.getEicMessage));
  } else {
    yield put(setEicMessages({ loading: false, messages: [] }));
  }
}

export function* watchChangePlanOffers() {
  yield takeLatest(getPlans.type, fetchChangePlanOffers);
  yield takeLatest(refetchPlans.type, refetchChangePlanOffers);
  yield takeLatest(addPlanToCart.type, addToCart);
  yield takeLatest(submitOrder.type, placeOrder);
  yield takeLatest(getHasPendingTransition.type, fetchHasPendingTransition);
  yield takeLatest(getEicMessages.type, getEicMessagesSaga);
}
